/**
 * CompanySetupInputsCrud — 3-level tree CRUD for Country → Region →
 * Authority. (iter-220, Feb 2026)
 *
 * TreeNode is extracted into ./parts/CompanySetupInputsTreeNode.jsx
 * because inlining a recursive JSX component here trips a Babel
 * traversal RangeError in this project's CRA plugin chain.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Plus, Loader2, X, Check, Languages } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useLanguage } from '../context/LanguageContext';
import DashboardHeader from '../components/DashboardHeader';
import BreadcrumbNav from '../components/BreadcrumbNav';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import TreeNode from './parts/CompanySetupInputsTreeNode';

const API = process.env.REACT_APP_BACKEND_URL;
const authHeader = () => {
  const token = sessionStorage.getItem('admin_token') || localStorage.getItem('admin_token');
  return { Authorization: 'Bearer ' + token };
};

export default function CompanySetupInputsCrud() {
  const { language } = useLanguage();
  const isAr = language === 'ar';

  const [tree, setTree] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [modal, setModal] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(API + '/api/admin/company-setup-inputs/tree', { headers: authHeader() });
      setTree((res.data && res.data.data) || []);
    } catch (e) {
      toast.error(isAr ? 'فشل تحميل البيانات' : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [isAr]);

  useEffect(() => { load(); }, [load]);

  const toggle = (id) => setExpanded((p) => Object.assign({}, p, { [id]: !p[id] }));
  const openAdd = (level, parentId) => setModal({ mode: 'add', level, parentId, doc: null });
  const openEdit = (level, doc) => setModal({ mode: 'edit', level, parentId: doc.parent_id, doc });

  const toggleActive = async (item) => {
    try {
      await axios.put(
        API + '/api/admin/company-setup-inputs/' + item.id,
        { is_active: !item.is_active },
        { headers: authHeader() }
      );
      await load();
    } catch {
      toast.error(isAr ? 'فشل التحديث' : 'Update failed');
    }
  };

  const deleteItem = async (item) => {
    const label = isAr ? (item.name_ar || item.name_en) : (item.name_en || item.name_ar);
    const msg = isAr ? ('حذف "' + label + '" وكل ما تحته؟') : ('Delete "' + label + '" and all children?');
    if (!window.confirm(msg)) return;
    try {
      await axios.delete(API + '/api/admin/company-setup-inputs/' + item.id, { headers: authHeader() });
      toast.success(isAr ? 'تم الحذف' : 'Deleted');
      await load();
    } catch {
      toast.error(isAr ? 'فشل الحذف' : 'Delete failed');
    }
  };

  return (
    <div className="min-h-screen bg-[#030303]">
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[500px] bg-[#F5A55A]/5 rounded-full blur-[150px]" />
      </div>

      <DashboardHeader showBackButton={true} backPath="/control/company-setup" />

      <div className="border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <BreadcrumbNav items={[
            { label_ar: 'لوحة التحكم', label_en: 'Dashboard', path: '/dashboard' },
            { label_ar: 'CLOUDGM FZ LLC', label_en: 'CLOUDGM FZ LLC', path: '/dashboard?view=cloudgm' },
            { label_ar: 'التحكم والمعلومات', label_en: 'Control & Info', path: '/control/cloudgm' },
            { label_ar: 'تأسيس الشركات', label_en: 'Company Setup', path: '/control/company-setup' },
            { label_ar: 'قائمة مدخلات تسجيل الشركات', label_en: 'Registration Inputs', path: null }
          ]} />
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-white mb-1">
              {isAr ? 'قائمة مدخلات تسجيل الشركات' : 'Company Registration Inputs'}
            </h1>
            <p className="text-sm text-[#71717A]">
              {isAr
                ? 'ثلاث قوائم منسدلة متتابعة: الدولة ← الإمارة/المنطقة ← الجهة'
                : 'Three cascading dropdowns: Country → Region → Authority'}
            </p>
          </div>
          <Button
            onClick={() => openAdd('country', null)}
            className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl gap-2"
            data-testid="add-country-btn"
          >
            <Plus className="h-4 w-4" />
            {isAr ? 'إضافة دولة' : 'Add Country'}
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-[#F5A55A]" />
          </div>
        ) : null}

        {!loading && tree.length === 0 ? (
          <div className="rounded-2xl border border-white/10 bg-[#151530] p-12 text-center text-[#71717A]">
            {isAr ? 'لا توجد دول. أضف الأولى.' : 'No countries yet. Add the first one.'}
          </div>
        ) : null}

        {!loading && tree.length > 0 ? (
          <div className="space-y-3">
            {tree.map((country) => (
              <TreeNode
                key={country.id}
                node={country}
                level="country"
                expanded={expanded}
                onToggle={toggle}
                onAddChild={openAdd}
                onEdit={openEdit}
                onDelete={deleteItem}
                onToggleActive={toggleActive}
                isAr={isAr}
              >
                {expanded[country.id] && (country.regions || []).map((region) => (
                  <TreeNode
                    key={region.id}
                    node={region}
                    level="region"
                    expanded={expanded}
                    onToggle={toggle}
                    onAddChild={openAdd}
                    onEdit={openEdit}
                    onDelete={deleteItem}
                    onToggleActive={toggleActive}
                    isAr={isAr}
                  >
                    {expanded[region.id] && (region.authorities || []).map((auth) => (
                      <TreeNode
                        key={auth.id}
                        node={auth}
                        level="authority"
                        expanded={expanded}
                        onToggle={toggle}
                        onAddChild={openAdd}
                        onEdit={openEdit}
                        onDelete={deleteItem}
                        onToggleActive={toggleActive}
                        isAr={isAr}
                      />
                    ))}
                  </TreeNode>
                ))}
              </TreeNode>
            ))}
          </div>
        ) : null}
      </div>

      {modal ? (
        <EditorDialog
          modal={modal}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); load(); }}
          isAr={isAr}
        />
      ) : null}
    </div>
  );
}

// ── Add/Edit dialog ────────────────────────────────────────────────
function EditorDialog(props) {
  const { modal, onClose, onSaved, isAr } = props;
  const [form, setForm] = useState(() => ({
    name_ar: (modal.doc && modal.doc.name_ar) || '',
    name_en: (modal.doc && modal.doc.name_en) || '',
    code: (modal.doc && modal.doc.code) || '',
    is_active: modal.doc ? modal.doc.is_active : true,
  }));
  const [saving, setSaving] = useState(false);
  const [translating, setTranslating] = useState(null);

  const label = modal.level === 'country'
    ? (isAr ? 'دولة' : 'Country')
    : modal.level === 'region'
      ? (isAr ? 'إمارة / منطقة' : 'Region')
      : (isAr ? 'جهة' : 'Authority');

  const translate = async (from) => {
    const source = from === 'ar' ? form.name_ar : form.name_en;
    if (!source.trim()) {
      toast.error(isAr ? 'أدخل النص أولاً' : 'Enter text first');
      return;
    }
    setTranslating(from);
    try {
      const res = await axios.post(
        API + '/api/utils/translate',
        { text: source, source: from, target: from === 'ar' ? 'en' : 'ar' },
        { headers: authHeader() }
      );
      const translated = (res.data && res.data.translated) || '';
      if (from === 'ar') setForm((p) => Object.assign({}, p, { name_en: translated }));
      else setForm((p) => Object.assign({}, p, { name_ar: translated }));
    } catch (e) {
      toast.error(isAr ? 'فشل الترجمة' : 'Translation failed');
    } finally {
      setTranslating(null);
    }
  };

  const save = async () => {
    if (!form.name_ar.trim() || !form.name_en.trim()) {
      toast.error(isAr ? 'الاسم عربي وإنجليزي مطلوب' : 'Both Arabic and English names are required');
      return;
    }
    setSaving(true);
    try {
      if (modal.mode === 'add') {
        await axios.post(API + '/api/admin/company-setup-inputs', {
          level: modal.level,
          parent_id: modal.parentId,
          name_ar: form.name_ar,
          name_en: form.name_en,
          code: form.code,
          is_active: form.is_active,
        }, { headers: authHeader() });
        toast.success(isAr ? 'تمت الإضافة' : 'Added');
      } else {
        await axios.put(API + '/api/admin/company-setup-inputs/' + modal.doc.id, {
          name_ar: form.name_ar,
          name_en: form.name_en,
          code: form.code,
          is_active: form.is_active,
        }, { headers: authHeader() });
        toast.success(isAr ? 'تم التحديث' : 'Updated');
      }
      onSaved();
    } catch (e) {
      const msg = (e && e.response && e.response.data && e.response.data.detail) || (isAr ? 'فشل الحفظ' : 'Save failed');
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-[100] overflow-y-auto p-4 flex items-start sm:items-center justify-center">
      <div className="bg-[#1a1a2e] rounded-2xl border border-[#2a2a4e] w-full max-w-lg my-6 sm:my-8">
        <div className="flex items-center justify-between p-6 border-b border-[#2a2a4e]">
          <h2 className="text-lg font-bold text-white">
            {modal.mode === 'add'
              ? ((isAr ? 'إضافة ' : 'Add ') + label)
              : ((isAr ? 'تعديل ' : 'Edit ') + label)}
          </h2>
          <button onClick={onClose} className="w-8 h-8 rounded-lg bg-[#0a0a0a] flex items-center justify-center hover:bg-red-500/20">
            <X className="h-4 w-4 text-[#808080]" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm text-[#808080] mb-2">
              {isAr ? 'الاسم بالعربية' : 'Arabic Name'} *
            </label>
            <div className="flex gap-2">
              <Input
                value={form.name_ar}
                onChange={(e) => setForm(Object.assign({}, form, { name_ar: e.target.value }))}
                className="bg-[#0a0a0a] border-[#2a2a4e] text-white rounded-xl flex-1"
                data-testid="input-name-ar"
              />
              <Button
                type="button"
                onClick={() => translate('ar')}
                disabled={!form.name_ar.trim() || translating === 'ar'}
                variant="outline"
                size="sm"
                className="border-[#2a2a4e] text-blue-400 hover:bg-blue-500/20 w-10 h-10 p-0"
                title={isAr ? 'ترجمة إلى الإنجليزية' : 'Translate to English'}
              >
                {translating === 'ar' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Languages className="h-4 w-4" />}
              </Button>
            </div>
          </div>

          <div>
            <label className="block text-sm text-[#808080] mb-2">
              {isAr ? 'الاسم بالإنجليزية' : 'English Name'} *
            </label>
            <div className="flex gap-2">
              <Input
                value={form.name_en}
                onChange={(e) => setForm(Object.assign({}, form, { name_en: e.target.value }))}
                className="bg-[#0a0a0a] border-[#2a2a4e] text-white rounded-xl flex-1"
                dir="ltr"
                data-testid="input-name-en"
              />
              <Button
                type="button"
                onClick={() => translate('en')}
                disabled={!form.name_en.trim() || translating === 'en'}
                variant="outline"
                size="sm"
                className="border-[#2a2a4e] text-blue-400 hover:bg-blue-500/20 w-10 h-10 p-0"
                title={isAr ? 'ترجمة إلى العربية' : 'Translate to Arabic'}
              >
                {translating === 'en' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Languages className="h-4 w-4" />}
              </Button>
            </div>
          </div>

          <div>
            <label className="block text-sm text-[#808080] mb-2">
              {isAr ? 'الكود (اختياري)' : 'Code (optional)'}
            </label>
            <Input
              value={form.code}
              onChange={(e) => setForm(Object.assign({}, form, { code: e.target.value }))}
              placeholder={modal.level === 'country' ? 'UAE / SA / KW ...' : 'e.g. RAKEZ'}
              className="bg-[#0a0a0a] border-[#2a2a4e] text-white rounded-xl"
              dir="ltr"
              data-testid="input-code"
            />
          </div>

          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={form.is_active}
              onChange={(e) => setForm(Object.assign({}, form, { is_active: e.target.checked }))}
              className="w-4 h-4"
            />
            <span className="text-sm text-white">
              {isAr ? 'مفعّل (يظهر في القوائم المنسدلة)' : 'Active (shows in dropdowns)'}
            </span>
          </label>
        </div>

        <div className="flex items-center justify-end gap-3 p-6 border-t border-[#2a2a4e]">
          <Button onClick={onClose} variant="outline" className="border-[#2a2a4e] text-white hover:bg-[#2a2a4e]">
            {isAr ? 'إلغاء' : 'Cancel'}
          </Button>
          <Button onClick={save} disabled={saving} className="bg-emerald-500 hover:bg-emerald-600 text-white gap-2" data-testid="save-input-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
            {modal.mode === 'add' ? (isAr ? 'إضافة' : 'Add') : (isAr ? 'تحديث' : 'Update')}
          </Button>
        </div>
      </div>
    </div>
  );
}
